import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import RefreshIcon from "../../../../components/icons/RefreshIcon";
import SelectInput from "../../../../components/inputs/selectInput/SelectInput";
import Loader from "../../../../components/loader/Loader";
import SettingsPagesContainer from "../../../../components/settingsPagesContainer/SettingsPagesContainer";
import {
  fetchActiveUsersComparisonStats,
  fetchTournamentRegistrationAnalytics,
  fetchUsersSinceDateStats,
  fetchUserRegistrationStatsSection,
  fetchUserRegistrationStats,
} from "../../../../features/adminStatistics/adminStatisticsSlice";
import styles from "./statistics.module.css";

const arabicMonths = [
  { value: 1, label: "يناير" },
  { value: 2, label: "فبراير" },
  { value: 3, label: "مارس" },
  { value: 4, label: "أبريل" },
  { value: 5, label: "مايو" },
  { value: 6, label: "يونيو" },
  { value: 7, label: "يوليو" },
  { value: 8, label: "أغسطس" },
  { value: 9, label: "سبتمبر" },
  { value: 10, label: "أكتوبر" },
  { value: 11, label: "نوفمبر" },
  { value: 12, label: "ديسمبر" },
];

const formatNumber = (value) =>
  Number(value || 0).toLocaleString("ar-EG", {
    maximumFractionDigits: 0,
  });

const formatArabicDigits = (value) =>
  Number(value || 0).toLocaleString("ar-EG", {
    maximumFractionDigits: 0,
  });

const formatSignedNumber = (value) => {
  const numericValue = Number(value || 0);
  if (numericValue > 0) return `+${formatNumber(numericValue)}`;
  if (numericValue < 0) return `-${formatNumber(Math.abs(numericValue))}`;
  return formatNumber(0);
};

const formatPercent = (value) => {
  if (value === null || value === undefined) return "100%+";

  const numericValue = Number(value || 0);
  const formatted = numericValue.toLocaleString("ar-EG", {
    maximumFractionDigits: 2,
  });

  return numericValue > 0 ? `+${formatted}٪` : `${formatted}٪`;
};

const comparisonTimeframeOptions = [
  {
    key: "today_so_far",
    label: "اليوم حتى الآن",
    currentLabel: "اليوم حتى الآن",
    previousLabel: "أمس (نفس الوقت)",
  },
  {
    key: "yesterday_vs_same_day_last_week",
    label: "أمس مقابل نفس اليوم الأسبوع الماضي",
    currentLabel: "أمس",
    previousLabel: "نفس اليوم الأسبوع الماضي",
  },
  {
    key: "last_7_days_vs_previous_7_days",
    label: "آخر ٧ أيام مقابل ٧ أيام السابقة",
    currentLabel: "آخر ٧ أيام",
    previousLabel: "٧ أيام السابقة",
  },
  {
    key: "last_28_days_vs_previous_28_days",
    label: "آخر ٢٨ يومًا مقابل ٢٨ يومًا السابقة",
    currentLabel: "آخر ٢٨ يومًا",
    previousLabel: "٢٨ يومًا السابقة",
  },
];

const donutLegendFormatter = (value) => (
  <span style={{ marginInlineStart: 6, color: "var(--chart-legend-text)" }}>
    {value}
  </span>
);

const defaultChartPalette = [
  "var(--chart-1)",
  "var(--chart-2)",
  "var(--chart-3)",
  "var(--chart-4)",
  "var(--chart-5)",
  "var(--chart-6)",
  "var(--chart-7)",
  "var(--chart-8)",
  "var(--chart-9)",
];
const comparisonPreviousPatternId = "statistics-comparison-previous-pattern";

const formatTournamentOptionLabel = (option = {}) => {
  const name = String(option?.name || "بطولة").trim() || "بطولة";
  const serialNumber = Number(option?.serialNumber || 0);

  if (serialNumber > 0) {
    return `#${formatArabicDigits(serialNumber)} - ${name}`;
  }

  return name;
};

const Statistics = () => {
  const dispatch = useDispatch();
  const {
    totalUsers,
    todayRegisteredCount,
    todayActiveUsersCount,
    monthlySeries,
    latestTournamentPlayersSeries,
    regionUsersSeries,
    osUsersSeries,
    selected,
    fetchStatus,
    activeComparison,
    activeComparisonStatus,
    activeComparisonError,
    usersSinceDateStats,
    usersSinceDateStatsStatus,
    usersSinceDateStatsError,
    tournamentRegistrationAnalytics,
    tournamentRegistrationAnalyticsStatus,
    tournamentRegistrationAnalyticsError,
  } = useSelector((state) => state.adminStatistics);

  const now = useMemo(() => new Date(), []);
  const [selectedYear, setSelectedYear] = useState(now.getUTCFullYear());
  const [panelRefreshState, setPanelRefreshState] = useState({
    monthly: false,
    latestTournamentPlayers: false,
    regionUsers: false,
    osUsers: false,
  });
  const [selectedFromDate, setSelectedFromDate] = useState(
    now.toISOString().slice(0, 10),
  );
  const [selectedComparisonTimeframe, setSelectedComparisonTimeframe] = useState(
    "last_7_days_vs_previous_7_days",
  );
  const [isComparisonMenuOpen, setIsComparisonMenuOpen] = useState(false);
  const comparisonMenuRef = useRef(null);
  const chartTheme = useMemo(
    () => ({
      palette: defaultChartPalette,
      grid: "var(--chart-grid)",
      axis: "var(--chart-axis)",
      tooltipBackground: "var(--chart-tooltip-bg)",
      tooltipBorder: "var(--chart-tooltip-border)",
      tooltipText: "var(--chart-legend-text)",
      primaryLine: "var(--chart-1)",
      monthlyLine: "var(--chart-secondary-line)",
      neutralLine: "var(--chart-neutral-line)",
      mutedBar: "var(--chart-muted-bar)",
      comparisonCurrent: "var(--chart-comparison-current)",
      comparisonPreviousFill: "var(--chart-comparison-previous-fill)",
      comparisonPreviousStripe: "var(--chart-comparison-previous-stripe)",
      hoverOverlay: "var(--chart-hover-overlay)",
      registrationAgeHoverOverlay: "var(--chart-registration-age-hover-overlay)",
      registrationAgePalette: [
        "var(--chart-registration-age-1)",
        "var(--chart-registration-age-2)",
        "var(--chart-registration-age-3)",
        "var(--chart-registration-age-4)",
        "var(--chart-registration-age-5)",
        "var(--chart-registration-age-6)",
      ],
      positiveTrend: "var(--chart-positive-trend)",
      donutStroke: "var(--chart-donut-divider)",
      android: "var(--color-success)",
      ios: "var(--chart-9)",
      fallback: "var(--color-text-tertiary)",
      legendText: "var(--chart-legend-text)",
    }),
    [],
  );
  const tooltipContentStyle = useMemo(
    () => ({
      borderRadius: "8px",
      border: `1px solid ${chartTheme.tooltipBorder}`,
      background: chartTheme.tooltipBackground,
      textAlign: "right",
      direction: "rtl",
      color: chartTheme.tooltipText,
    }),
    [chartTheme],
  );
  const tooltipLabelStyle = useMemo(
    () => ({
      color: chartTheme.tooltipText,
      fontWeight: 700,
    }),
    [chartTheme],
  );
  const tooltipItemStyle = useMemo(
    () => ({
      color: chartTheme.tooltipText,
    }),
    [chartTheme],
  );

  const runSectionRefresh = useCallback(async (panelKey, action) => {
    setPanelRefreshState((prev) => ({
      ...prev,
      [panelKey]: true,
    }));

    try {
      await action();
    } finally {
      setPanelRefreshState((prev) => ({
        ...prev,
        [panelKey]: false,
      }));
    }
  }, []);

  const handleRefreshMonthlyStats = useCallback(
    async () =>
      runSectionRefresh("monthly", () =>
        dispatch(
          fetchUserRegistrationStatsSection({
            year: selectedYear,
            section: "monthly",
          }),
        ).unwrap(),
      ),
    [dispatch, runSectionRefresh, selectedYear],
  );

  useEffect(() => {
    dispatch(
      fetchUserRegistrationStats({
        year: now.getUTCFullYear(),
      }),
    );
  }, [dispatch, now]);

  useEffect(() => {
    dispatch(
      fetchActiveUsersComparisonStats({
        timeframe: selectedComparisonTimeframe,
      }),
    );
  }, [dispatch, selectedComparisonTimeframe]);

  const handleFetchUsersSinceDateStats = useCallback(async () => {
    if (!selectedFromDate) return;

    await dispatch(
      fetchUsersSinceDateStats({
        fromDate: selectedFromDate,
      }),
    ).unwrap();
  }, [dispatch, selectedFromDate]);

  const handleRefreshComparisonStats = useCallback(
    async () =>
      dispatch(
        fetchActiveUsersComparisonStats({
          timeframe: selectedComparisonTimeframe,
        }),
      ).unwrap(),
    [dispatch, selectedComparisonTimeframe],
  );

  const handleRefreshLatestTournamentPlayers = useCallback(
    async () =>
      runSectionRefresh("latestTournamentPlayers", () =>
        dispatch(
          fetchUserRegistrationStatsSection({
            section: "latestTournamentPlayers",
            forceRefresh: true,
          }),
        ).unwrap(),
      ),
    [dispatch, runSectionRefresh],
  );

  const handleRefreshRegionUsers = useCallback(
    async () =>
      runSectionRefresh("regionUsers", () =>
        dispatch(
          fetchUserRegistrationStatsSection({
            section: "regionUsers",
            forceRefresh: true,
          }),
        ).unwrap(),
      ),
    [dispatch, runSectionRefresh],
  );

  const handleRefreshOsUsers = useCallback(
    async () =>
      runSectionRefresh("osUsers", () =>
        dispatch(
          fetchUserRegistrationStatsSection({
            section: "osUsers",
            forceRefresh: true,
          }),
        ).unwrap(),
      ),
    [dispatch, runSectionRefresh],
  );

  useEffect(() => {
    if (!selectedFromDate) return;

    dispatch(
      fetchUsersSinceDateStats({
        fromDate: selectedFromDate,
      }),
    );
  }, [dispatch, selectedFromDate]);

  useEffect(() => {
    dispatch(fetchTournamentRegistrationAnalytics());
  }, [dispatch]);

  useEffect(() => {
    if (!isComparisonMenuOpen) return undefined;

    const handleOutsideClick = (event) => {
      if (!comparisonMenuRef.current?.contains(event.target)) {
        setIsComparisonMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [isComparisonMenuOpen]);

  const yearOptions = useMemo(() => {
    const currentYear = now.getUTCFullYear();
    return Array.from({ length: 6 }, (_, index) => currentYear - index);
  }, [now]);

  const chartData = useMemo(() => {
    const monthNameByValue = new Map(arabicMonths.map((item) => [item.value, item.label]));

    return (Array.isArray(monthlySeries) ? monthlySeries : []).map((item) => {
      const monthValue = Number(item?.month || 0);
      const monthName = monthNameByValue.get(monthValue) || "غير معروف";

      return {
        ...item,
        count: Number(item?.count || 0),
        chartLabel: `${monthName} (${formatArabicDigits(monthValue)})`,
      };
    });
  }, [monthlySeries]);

  const latestTournamentPlayersChartData = useMemo(() => {
    const shrinkName = (name = "") => {
      const trimmed = String(name || "").trim();
      if (!trimmed) return "بطولة";
      if (trimmed.length <= 16) return trimmed;
      return `${trimmed.slice(0, 16)}...`;
    };

    return (Array.isArray(latestTournamentPlayersSeries)
      ? latestTournamentPlayersSeries
      : []
    ).map((item, index) => {
      const serial = Number(item?.serialNumber || 0);
      const fallbackIndex = index + 1;
      const labelSeed = serial > 0 ? `#${formatArabicDigits(serial)}` : `#${formatArabicDigits(fallbackIndex)}`;

      return {
        ...item,
        playersCount: Number(item?.playersCount || 0),
        chartLabel: labelSeed,
        tournamentLabel: shrinkName(item?.tournamentName || "بطولة"),
      };
    });
  }, [latestTournamentPlayersSeries]);

  const regionDonutData = useMemo(() => {
    return (Array.isArray(regionUsersSeries) ? regionUsersSeries : [])
      .map((item, index) => ({
        name: String(item?.region || "غير محدد"),
        value: Number(item?.count || 0),
        fill: chartTheme.palette[index % chartTheme.palette.length],
      }))
      .filter((item) => item.value > 0);
  }, [chartTheme.palette, regionUsersSeries]);

  const osDonutData = useMemo(() => {
    const colorByOs = {
      android: chartTheme.android,
      ios: chartTheme.ios,
    };

    return (Array.isArray(osUsersSeries) ? osUsersSeries : [])
      .map((item) => {
        const osKey = String(item?.os || "").toLowerCase();
        const label = osKey === "android" ? "Android" : osKey === "ios" ? "iOS" : "Other";

        return {
          name: label,
          value: Number(item?.count || 0),
          fill: colorByOs[osKey] || chartTheme.fallback,
        };
      })
      .filter((item) => item.value > 0);
  }, [chartTheme.android, chartTheme.fallback, chartTheme.ios, osUsersSeries]);

  const tournamentRegistrationRegionDonutData = useMemo(() => {
    return (
      Array.isArray(tournamentRegistrationAnalytics?.regionSeries)
        ? tournamentRegistrationAnalytics.regionSeries
        : []
    )
      .map((item, index) => ({
        name: String(item?.region || "غير محدد"),
        value: Number(item?.count || 0),
        fill: chartTheme.palette[index % chartTheme.palette.length],
      }))
      .filter((item) => item.value > 0);
  }, [chartTheme.palette, tournamentRegistrationAnalytics?.regionSeries]);

  const tournamentRegistrationAgeChartData = useMemo(
    () =>
      (
        Array.isArray(tournamentRegistrationAnalytics?.registrationAgeSeries)
          ? tournamentRegistrationAnalytics.registrationAgeSeries
          : []
      ).map((item, index) => ({
        key: String(item?.key || ""),
        label: String(item?.label || "غير محدد"),
        count: Number(item?.count || 0),
        fill:
          chartTheme.registrationAgePalette[
            index % chartTheme.registrationAgePalette.length
          ],
      })),
    [
      chartTheme.registrationAgePalette,
      tournamentRegistrationAnalytics?.registrationAgeSeries,
    ],
  );

  const selectedComparisonOption = useMemo(
    () =>
      comparisonTimeframeOptions.find((option) => option.key === selectedComparisonTimeframe) ||
      comparisonTimeframeOptions[0],
    [selectedComparisonTimeframe],
  );

  const comparisonChartData = useMemo(() => {
    const selectedOption =
      comparisonTimeframeOptions.find((option) => option.key === selectedComparisonTimeframe) ||
      comparisonTimeframeOptions[0];
    const previous = activeComparison?.previous;
    const current = activeComparison?.current;

    if (!selectedOption || !previous || !current) return [];

    return [
      {
        key: "previous",
        label: selectedOption.previousLabel || "الفترة السابقة",
        count: Number(previous?.count || 0),
        fill: `url(#${comparisonPreviousPatternId})`,
      },
      {
        key: "current",
        label: selectedOption.currentLabel || "الفترة الحالية",
        count: Number(current?.count || 0),
        fill: chartTheme.comparisonCurrent,
      },
    ];
  }, [
    activeComparison,
    chartTheme.comparisonCurrent,
    selectedComparisonTimeframe,
  ]);

  const comparisonChangeValue = Number(activeComparison?.change?.value || 0);
  const comparisonChangePercent = activeComparison?.change?.percent;
  const comparisonDeltaClass =
    comparisonChangeValue > 0
      ? styles.positiveDelta
      : comparisonChangeValue < 0
        ? styles.negativeDelta
        : styles.neutralDelta;
  const isComparisonLoading = activeComparisonStatus === "loading";
  const isComparisonInitialLoading =
    isComparisonLoading && comparisonChartData.length === 0;
  const isSinceDateLoading = usersSinceDateStatsStatus === "loading";
  const isTournamentRegistrationAnalyticsLoading =
    tournamentRegistrationAnalyticsStatus === "loading";
  const selectedTournamentOptionLabel = formatTournamentOptionLabel(
    tournamentRegistrationAnalytics?.selected || {},
  );
  const selectedTournamentRegistrationId =
    tournamentRegistrationAnalytics?.selected?.tournamentId || "";
  const tournamentRegistrationTotalUsers = Number(
    tournamentRegistrationAnalytics?.totalRegisteredUsers || 0,
  );
  const handleRefreshTournamentRegistrationAnalytics = useCallback(
    async () =>
      dispatch(
        fetchTournamentRegistrationAnalytics({
          tournamentId: selectedTournamentRegistrationId || undefined,
        }),
      ).unwrap(),
    [dispatch, selectedTournamentRegistrationId],
  );
  const isMonthlyPanelRefreshing = panelRefreshState.monthly;
  const isLatestTournamentPlayersRefreshing =
    panelRefreshState.latestTournamentPlayers;
  const isRegionUsersRefreshing = panelRefreshState.regionUsers;
  const isOsUsersRefreshing = panelRefreshState.osUsers;
  const isComparisonPanelRefreshing =
    activeComparisonStatus === "loading" && comparisonChartData.length > 0;

  const isInitialLoading =
    fetchStatus === "idle" || (fetchStatus === "loading" && monthlySeries.length === 0);

  const renderRefreshButton = (title, onClick, disabled = false) => (
    <button
      type="button"
      className={styles.chartRefreshButton}
      onClick={() => {
        Promise.resolve(onClick?.()).catch(() => {});
      }}
      disabled={disabled}
      title={title}
      aria-label={title}
    >
      <RefreshIcon className={styles.chartRefreshIcon} />
    </button>
  );

  return (
    <SettingsPagesContainer title="الإحصائيات" fullWidth>
      <div className={styles.container}>
        <div className={styles.section}>
          <div className={styles.sectionHeader}>لوحة الإحصائيات</div>

          {isInitialLoading ? (
            <div className={styles.loaderContainer}>
              <Loader />
            </div>
          ) : (
            <>
              <div className={styles.cardsGrid}>
                <div className={styles.statCard}>
                  <span className={styles.cardLabel}>إجمالي المستخدمين المسجلين</span>
                  <span className={styles.cardValue}>{formatNumber(totalUsers)}</span>
                </div>
                <div className={styles.statCard}>
                  <span className={styles.cardLabel}>عدد المستخدمين المسجلين اليوم</span>
                  <span className={styles.cardValue}>{formatNumber(todayRegisteredCount)}</span>
                </div>
                <div className={styles.statCard}>
                  <span className={styles.cardLabel}>عدد المستخدمين النشطين اليوم</span>
                  <span className={styles.cardValue}>{formatNumber(todayActiveUsersCount)}</span>
                </div>
              </div>

              <div className={styles.chartPanelsGrid}>
                <div
                  className={`${styles.chartPanel} ${isMonthlyPanelRefreshing ? styles.chartPanelRefreshing : ""}`}
                >
                  <div className={styles.chartPanelHeader}>
                    <div className={styles.chartHeaderRow}>
                      <div className={styles.chartHeader}>
                        تسجيل المستخدمين في سنة{" "}
                        {formatArabicDigits(selected?.year || selectedYear)}
                      </div>
                      {renderRefreshButton(
                        "تحديث إحصائية تسجيل المستخدمين في السنة",
                        handleRefreshMonthlyStats,
                        isMonthlyPanelRefreshing,
                      )}
                    </div>
                    <div className={styles.chartToolbar}>
                      <select
                        value={selectedYear}
                        onChange={(event) => setSelectedYear(Number(event.target.value))}
                        className={`${styles.select} ${styles.compactSelect}`}
                      >
                        {yearOptions.map((year) => (
                          <option key={year} value={year}>
                            {year}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {monthlySeries.length === 0 ? (
                    <div className={styles.placeholderBody}>لا توجد بيانات لعرضها حالياً.</div>
                  ) : (
                    <div className={styles.chartContainer}>
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart
                          data={chartData}
                          margin={{ top: 10, right: 16, left: 8, bottom: 8 }}
                        >
                          <CartesianGrid stroke={chartTheme.grid} strokeDasharray="3 3" />
                          <XAxis
                            dataKey="chartLabel"
                            tick={{ fontSize: 11, fill: chartTheme.axis }}
                            interval="preserveStartEnd"
                            minTickGap={24}
                          />
                          <YAxis
                            allowDecimals={false}
                            tick={{ fontSize: 11, fill: chartTheme.axis }}
                            tickFormatter={formatNumber}
                          />
                          <Tooltip
                            formatter={(value) => [formatNumber(value), "عدد المستخدمين"]}
                            labelFormatter={(label, payload) => {
                              const item = payload?.[0]?.payload;
                              if (!item) return label;
                              return `${label} - ${formatArabicDigits(item?.year)}`;
                            }}
                            contentStyle={tooltipContentStyle}
                            labelStyle={tooltipLabelStyle}
                            itemStyle={tooltipItemStyle}
                          />
                          <Line
                            type="monotone"
                            dataKey="count"
                            stroke={chartTheme.monthlyLine}
                            strokeWidth={3}
                            dot={{ r: 4, fill: chartTheme.monthlyLine }}
                            activeDot={{ r: 6 }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  )}

                  {isMonthlyPanelRefreshing ? (
                    <div className={styles.chartRefreshOverlay}>
                      <Loader />
                    </div>
                  ) : null}
                </div>

                <div
                  className={`${styles.chartPanel} ${isComparisonLoading ? styles.chartPanelRefreshing : ""}`}
                >
                  <div className={styles.chartPanelHeader}>
                    <div className={styles.chartHeaderRow}>
                      <div className={styles.chartHeader}>مقارنة المستخدمين النشطين</div>
                      {renderRefreshButton(
                        "تحديث مقارنة المستخدمين النشطين",
                        handleRefreshComparisonStats,
                        isComparisonLoading,
                      )}
                    </div>
                    <div className={styles.chartToolbar}>
                      <div className={styles.timeframeMenuWrapper} ref={comparisonMenuRef}>
                        <button
                          type="button"
                          className={styles.timeframeMenuTrigger}
                          onClick={() => setIsComparisonMenuOpen((prev) => !prev)}
                          aria-expanded={isComparisonMenuOpen}
                          aria-haspopup="true"
                        >
                          <span>{selectedComparisonOption?.label}</span>
                          <span
                            className={`${styles.timeframeMenuCaret} ${isComparisonMenuOpen ? styles.timeframeMenuCaretOpen : ""}`}
                          >
                            ▾
                          </span>
                        </button>

                        {isComparisonMenuOpen ? (
                          <div className={styles.timeframeMenuList}>
                            {comparisonTimeframeOptions.map((option) => (
                              <button
                                key={option.key}
                                type="button"
                                className={`${styles.timeframeMenuItem} ${selectedComparisonTimeframe === option.key ? styles.timeframeMenuItemActive : ""}`}
                                onClick={() => {
                                  setSelectedComparisonTimeframe(option.key);
                                  setIsComparisonMenuOpen(false);
                                }}
                              >
                                {option.label}
                              </button>
                            ))}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </div>

                  {isComparisonInitialLoading ? (
                    <div className={styles.loaderContainer}>
                      <Loader />
                    </div>
                  ) : (
                    <>
                      <div className={styles.comparisonMetaRow}>
                        <span className={styles.comparisonMetaText}>
                          {selectedComparisonOption?.currentLabel || "الفترة الحالية"}:{" "}
                          {formatNumber(activeComparison?.current?.count || 0)} |{" "}
                          {selectedComparisonOption?.previousLabel || "الفترة السابقة"}:{" "}
                          {formatNumber(activeComparison?.previous?.count || 0)}
                        </span>
                        <span
                          className={`${styles.comparisonDelta} ${comparisonDeltaClass}`}
                        >
                          Δ {formatSignedNumber(comparisonChangeValue)} (
                          {formatPercent(comparisonChangePercent)})
                        </span>
                      </div>

                      {activeComparisonStatus === "failed" ? (
                        <div className={styles.placeholderBody}>
                          {activeComparisonError === "errors.server.validation"
                            ? "قيمة المدة الزمنية غير صحيحة."
                            : "تعذر تحميل بيانات المقارنة حالياً."}
                        </div>
                      ) : comparisonChartData.length === 0 ? (
                        <div className={styles.placeholderBody}>
                          لا توجد بيانات لعرضها حالياً.
                        </div>
                      ) : (
                        <div className={styles.chartContainer}>
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                              data={comparisonChartData}
                              margin={{ top: 10, right: 16, left: 8, bottom: 8 }}
                            >
                              <defs>
                                <pattern
                                  id={comparisonPreviousPatternId}
                                  width="8"
                                  height="8"
                                  patternUnits="userSpaceOnUse"
                                >
                                  <rect
                                    width="8"
                                    height="8"
                                    fill={chartTheme.comparisonPreviousFill}
                                  />
                                  <path
                                    d="M-2,2 l4,-4 M0,8 l8,-8 M6,10 l4,-4"
                                    stroke={chartTheme.comparisonPreviousStripe}
                                    strokeWidth="1.5"
                                  />
                                </pattern>
                              </defs>
                              <CartesianGrid stroke={chartTheme.grid} strokeDasharray="3 3" />
                              <XAxis
                                dataKey="label"
                                tick={{ fontSize: 11, fill: chartTheme.axis }}
                                interval={0}
                              />
                              <YAxis
                                allowDecimals={false}
                                tick={{ fontSize: 11, fill: chartTheme.axis }}
                                tickFormatter={formatNumber}
                              />
                              <Tooltip
                                formatter={(value) => [formatNumber(value), "مستخدم نشط"]}
                                contentStyle={tooltipContentStyle}
                                labelStyle={tooltipLabelStyle}
                                itemStyle={tooltipItemStyle}
                                cursor={{ fill: chartTheme.hoverOverlay }}
                              />
                              <Bar dataKey="count" radius={[8, 8, 0, 0]} barSize={48}>
                                {comparisonChartData.map((item) => (
                                  <Cell key={item.key} fill={item.fill} />
                                ))}
                              </Bar>
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      )}
                    </>
                  )}

                  {isComparisonPanelRefreshing ? (
                    <div className={styles.chartRefreshOverlay}>
                      <Loader />
                    </div>
                  ) : null}
                </div>

                <div
                  className={`${styles.chartPanel} ${styles.bottomChartPanel} ${
                    isLatestTournamentPlayersRefreshing
                      ? styles.chartPanelRefreshing
                      : ""
                  }`}
                >
                  <div className={styles.chartPanelHeader}>
                    <div className={styles.chartHeaderRow}>
                      <div className={styles.chartHeader}>
                        مقارنة عدد اللاعبين في آخر ١٠ بطولات
                      </div>
                      {renderRefreshButton(
                        "تحديث مقارنة عدد اللاعبين في آخر 10 بطولات",
                        handleRefreshLatestTournamentPlayers,
                        isLatestTournamentPlayersRefreshing,
                      )}
                    </div>
                  </div>
                  {latestTournamentPlayersChartData.length === 0 ? (
                    <div className={styles.placeholderBody}>لا توجد بيانات لعرضها حالياً.</div>
                  ) : (
                    <div className={styles.chartContainer}>
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart
                          data={latestTournamentPlayersChartData}
                          margin={{ top: 10, right: 16, left: 8, bottom: 8 }}
                        >
                          <CartesianGrid stroke={chartTheme.grid} strokeDasharray="3 3" />
                          <XAxis
                            dataKey="chartLabel"
                            tick={{ fontSize: 11, fill: chartTheme.axis }}
                            interval={0}
                            minTickGap={16}
                          />
                          <YAxis
                            allowDecimals={false}
                            tick={{ fontSize: 11, fill: chartTheme.axis }}
                            tickFormatter={formatNumber}
                          />
                          <Tooltip
                            formatter={(value) => [formatNumber(value), "عدد اللاعبين"]}
                            labelFormatter={(label, payload) => {
                              const item = payload?.[0]?.payload;
                              if (!item) return label;
                              return `${label} - ${item?.tournamentLabel || "بطولة"}`;
                            }}
                            contentStyle={tooltipContentStyle}
                            labelStyle={tooltipLabelStyle}
                            itemStyle={tooltipItemStyle}
                          />
                          <Line
                            type="monotone"
                            dataKey="playersCount"
                            stroke={chartTheme.primaryLine}
                            strokeWidth={3}
                            dot={{ r: 4, fill: chartTheme.primaryLine }}
                            activeDot={{ r: 6 }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  )}

                  {isLatestTournamentPlayersRefreshing ? (
                    <div className={styles.chartRefreshOverlay}>
                      <Loader />
                    </div>
                  ) : null}
                </div>

                <div
                  className={`${styles.chartPanel} ${isSinceDateLoading ? styles.chartPanelRefreshing : ""}`}
                >
                  <div className={styles.chartPanelHeader}>
                    <div className={styles.chartHeaderRow}>
                      <div className={styles.chartHeader}>
                        إحصائيات المستخدمين من تاريخ محدد
                      </div>
                      {renderRefreshButton(
                        "تحديث إحصائيات المستخدمين من تاريخ محدد",
                        handleFetchUsersSinceDateStats,
                        isSinceDateLoading,
                      )}
                    </div>
                    <div className={styles.chartToolbar}>
                      <input
                        type="date"
                        className={`${styles.select} ${styles.compactSelect}`}
                        value={selectedFromDate}
                        onChange={(event) => setSelectedFromDate(event.target.value)}
                        max={now.toISOString().slice(0, 10)}
                      />
                    </div>
                  </div>

                  {usersSinceDateStatsStatus === "failed" ? (
                    <div className={styles.placeholderBody}>
                      {usersSinceDateStatsError === "errors.server.validation"
                        ? "يرجى اختيار تاريخ صحيح."
                        : "تعذر تحميل البيانات حالياً."}
                    </div>
                  ) : (
                    <div className={styles.sinceDateStatsGrid}>
                      <div className={styles.sinceDateStatCard}>
                        <span className={styles.cardLabel}>عدد المسجلين منذ التاريخ المحدد</span>
                        <span className={styles.cardValue}>
                          {formatNumber(usersSinceDateStats?.registeredUsersCount || 0)}
                        </span>
                      </div>
                      <div className={styles.sinceDateStatCard}>
                        <span className={styles.cardLabel}>عدد النشطين منذ التاريخ المحدد</span>
                        <span className={styles.cardValue}>
                          {formatNumber(usersSinceDateStats?.activeUsersCount || 0)}
                        </span>
                      </div>
                      <div className={styles.sinceDateHint}>
                        النشاط محسوب إذا كان آخر نشاط للمستخدم خلال اليوم أو أمس.
                      </div>
                    </div>
                  )}

                  {isSinceDateLoading ? (
                    <div className={styles.chartRefreshOverlay}>
                      <Loader />
                    </div>
                  ) : null}
                </div>

                <div
                  className={`${styles.chartPanel} ${
                    isRegionUsersRefreshing ? styles.chartPanelRefreshing : ""
                  }`}
                >
                  <div className={styles.chartPanelHeader}>
                    <div className={styles.chartHeaderRow}>
                      <div className={styles.chartHeader}>
                        توزيع عدد المستخدمين حسب المنطقة
                      </div>
                      {renderRefreshButton(
                        "تحديث توزيع عدد المستخدمين حسب المنطقة",
                        handleRefreshRegionUsers,
                        isRegionUsersRefreshing,
                      )}
                    </div>
                  </div>

                  {regionDonutData.length === 0 ? (
                    <div className={styles.placeholderBody}>لا توجد بيانات لعرضها حالياً.</div>
                  ) : (
                    <div className={styles.chartContainer}>
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Tooltip
                            formatter={(value) => [formatNumber(value), "عدد المستخدمين"]}
                            contentStyle={tooltipContentStyle}
                            labelStyle={tooltipLabelStyle}
                            itemStyle={tooltipItemStyle}
                          />
                          <Legend
                            verticalAlign="bottom"
                            align="center"
                            wrapperStyle={{
                              direction: "rtl",
                              fontSize: "12px",
                              color: chartTheme.legendText,
                            }}
                            formatter={donutLegendFormatter}
                          />
                          <Pie
                            data={regionDonutData}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="46%"
                            outerRadius={78}
                            innerRadius={44}
                            paddingAngle={2}
                            stroke={chartTheme.donutStroke}
                            strokeWidth={1.25}
                          >
                            {regionDonutData.map((item) => (
                              <Cell key={item.name} fill={item.fill} />
                            ))}
                          </Pie>
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  )}

                  {isRegionUsersRefreshing ? (
                    <div className={styles.chartRefreshOverlay}>
                      <Loader />
                    </div>
                  ) : null}
                </div>

                <div
                  className={`${styles.chartPanel} ${
                    isOsUsersRefreshing ? styles.chartPanelRefreshing : ""
                  }`}
                >
                  <div className={styles.chartPanelHeader}>
                    <div className={styles.chartHeaderRow}>
                      <div className={styles.chartHeader}>
                        توزيع المستخدمين حسب نظام التشغيل
                      </div>
                      {renderRefreshButton(
                        "تحديث توزيع المستخدمين حسب نظام التشغيل",
                        handleRefreshOsUsers,
                        isOsUsersRefreshing,
                      )}
                    </div>
                  </div>

                  {osDonutData.length === 0 ? (
                    <div className={styles.placeholderBody}>لا توجد بيانات لعرضها حالياً.</div>
                  ) : (
                    <div className={styles.chartContainer}>
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Tooltip
                            formatter={(value) => [formatNumber(value), "عدد المستخدمين"]}
                            contentStyle={tooltipContentStyle}
                            labelStyle={tooltipLabelStyle}
                            itemStyle={tooltipItemStyle}
                          />
                          <Legend
                            verticalAlign="bottom"
                            align="center"
                            wrapperStyle={{
                              direction: "rtl",
                              fontSize: "12px",
                              color: chartTheme.legendText,
                            }}
                            formatter={donutLegendFormatter}
                          />
                          <Pie
                            data={osDonutData}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="46%"
                            outerRadius={78}
                            innerRadius={44}
                            paddingAngle={2}
                            stroke={chartTheme.donutStroke}
                            strokeWidth={1.25}
                          >
                            {osDonutData.map((item) => (
                              <Cell key={item.name} fill={item.fill} />
                            ))}
                          </Pie>
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  )}

                  {isOsUsersRefreshing ? (
                    <div className={styles.chartRefreshOverlay}>
                      <Loader />
                    </div>
                  ) : null}
                </div>

                <div
                  className={`${styles.chartPanel} ${styles.chartPanelWide} ${
                    isTournamentRegistrationAnalyticsLoading
                      ? styles.chartPanelRefreshing
                      : ""
                  }`}
                >
                  <div className={styles.chartPanelHeader}>
                    <div className={styles.chartHeaderRow}>
                      <div className={styles.chartHeader}>
                        تحليل المسجلين في بطولة محددة
                      </div>
                      {renderRefreshButton(
                        "تحديث تحليل المسجلين في بطولة محددة",
                        handleRefreshTournamentRegistrationAnalytics,
                        isTournamentRegistrationAnalyticsLoading,
                      )}
                    </div>
                    <div className={styles.chartToolbar}>
                      <SelectInput
                        className={`${styles.select} ${styles.tournamentAnalyticsSelect}`}
                        value={selectedTournamentRegistrationId}
                        setValue={(nextValue) =>
                          dispatch(
                            fetchTournamentRegistrationAnalytics({
                              tournamentId: nextValue || undefined,
                            }),
                          )
                        }
                        noPlaceHolder
                        searchable
                        searchThreshold={0}
                        searchPlaceholder="ابحث عن بطولة"
                        emptyResultsLabel="لا توجد بطولات مطابقة"
                        disabled={
                          !Array.isArray(
                            tournamentRegistrationAnalytics?.tournamentOptions,
                          ) ||
                          tournamentRegistrationAnalytics.tournamentOptions.length === 0
                        }
                      >
                        {(Array.isArray(
                          tournamentRegistrationAnalytics?.tournamentOptions,
                        )
                          ? tournamentRegistrationAnalytics.tournamentOptions
                          : []
                        ).map((option) => (
                          <option
                            key={option.tournamentId}
                            value={option.tournamentId}
                          >
                            {formatTournamentOptionLabel(option)}
                          </option>
                        ))}
                      </SelectInput>
                    </div>
                  </div>

                  {tournamentRegistrationAnalyticsStatus === "failed" ? (
                    <div className={styles.placeholderBody}>
                      {tournamentRegistrationAnalyticsError ===
                      "errors.server.validation"
                        ? "قيمة البطولة المحددة غير صحيحة."
                        : "تعذر تحميل تحليل المسجلين حالياً."}
                    </div>
                  ) : !Array.isArray(
                      tournamentRegistrationAnalytics?.tournamentOptions,
                    ) ||
                    tournamentRegistrationAnalytics.tournamentOptions.length === 0 ? (
                    <div className={styles.placeholderBody}>
                      لا توجد بطولات لعرضها حالياً.
                    </div>
                  ) : tournamentRegistrationTotalUsers === 0 ? (
                    <div className={styles.placeholderBody}>
                      لا يوجد مستخدمون مسجلون في البطولة المحددة.
                    </div>
                  ) : (
                    <>
                      <div className={styles.comparisonMetaRow}>
                        <span className={styles.comparisonMetaText}>
                          البطولة المحددة: {selectedTournamentOptionLabel}
                        </span>
                        <span className={styles.comparisonMetaText}>
                          إجمالي المسجلين:{" "}
                          {formatNumber(tournamentRegistrationTotalUsers)}
                        </span>
                      </div>

                      <div className={styles.analyticsDualGrid}>
                        <div className={styles.analyticsSubPanel}>
                          <div className={styles.analyticsSubPanelTitle}>
                            توزيع المناطق للمسجلين
                          </div>
                          {tournamentRegistrationRegionDonutData.length === 0 ? (
                            <div className={styles.placeholderBody}>
                              لا توجد بيانات مناطق لعرضها.
                            </div>
                          ) : (
                            <div className={styles.analyticsChartContainer}>
                              <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                  <Tooltip
                                    formatter={(value) => [
                                      formatNumber(value),
                                      "عدد المستخدمين",
                                    ]}
                                    contentStyle={tooltipContentStyle}
                                    labelStyle={tooltipLabelStyle}
                                    itemStyle={tooltipItemStyle}
                                  />
                                  <Legend
                                    verticalAlign="bottom"
                                    align="center"
                                    wrapperStyle={{
                                      direction: "rtl",
                                      fontSize: "12px",
                                      color: chartTheme.legendText,
                                    }}
                                    formatter={donutLegendFormatter}
                                  />
                                  <Pie
                                    data={tournamentRegistrationRegionDonutData}
                                    dataKey="value"
                                    nameKey="name"
                                    cx="50%"
                                    cy="46%"
                                    outerRadius={78}
                                    innerRadius={44}
                                    paddingAngle={2}
                                    stroke={chartTheme.donutStroke}
                                    strokeWidth={1.25}
                                  >
                                    {tournamentRegistrationRegionDonutData.map(
                                      (item) => (
                                        <Cell
                                          key={item.name}
                                          fill={item.fill}
                                        />
                                      ),
                                    )}
                                  </Pie>
                                </PieChart>
                              </ResponsiveContainer>
                            </div>
                          )}
                        </div>

                        <div className={styles.analyticsSubPanel}>
                          <div className={styles.analyticsSubPanelTitle}>
                            وقت تسجيل هؤلاء المستخدمين
                          </div>
                          <div className={styles.analyticsChartContainer}>
                            <ResponsiveContainer width="100%" height="100%">
                              <BarChart
                                data={tournamentRegistrationAgeChartData}
                                margin={{
                                  top: 10,
                                  right: 16,
                                  left: 8,
                                  bottom: 20,
                                }}
                              >
                                <CartesianGrid
                                  stroke={chartTheme.grid}
                                  strokeDasharray="3 3"
                                />
                                <XAxis
                                  dataKey="label"
                                  tick={{ fontSize: 11, fill: chartTheme.axis }}
                                  interval={0}
                                  angle={-15}
                                  textAnchor="end"
                                  height={54}
                                />
                                <YAxis
                                  allowDecimals={false}
                                  tick={{ fontSize: 11, fill: chartTheme.axis }}
                                  tickFormatter={formatNumber}
                                />
                                <Tooltip
                                  formatter={(value) => [
                                    formatNumber(value),
                                    "عدد المستخدمين",
                                  ]}
                                  contentStyle={tooltipContentStyle}
                                  labelStyle={tooltipLabelStyle}
                                  itemStyle={tooltipItemStyle}
                                  cursor={{
                                    fill: chartTheme.registrationAgeHoverOverlay,
                                  }}
                                />
                                <Bar
                                  dataKey="count"
                                  radius={[8, 8, 0, 0]}
                                  barSize={42}
                                >
                                  {tournamentRegistrationAgeChartData.map(
                                    (item) => (
                                      <Cell
                                        key={item.key}
                                        fill={item.fill}
                                      />
                                    ),
                                  )}
                                </Bar>
                              </BarChart>
                            </ResponsiveContainer>
                          </div>
                        </div>
                      </div>
                    </>
                  )}

                  {isTournamentRegistrationAnalyticsLoading ? (
                    <div className={styles.chartRefreshOverlay}>
                      <Loader />
                    </div>
                  ) : null}
                </div>
              </div>
            </>
          )}

        </div>
      </div>
    </SettingsPagesContainer>
  );
};

export default Statistics;
